import { useMemo } from "react";
import { ColDef, ICellEditorParams, ICellRendererParams, NewValueParams } from "ag-grid-community";

import { TGridApi } from "../../../../../../../../shared/contracts/TGridApi";
import IPivotChildColumn from "../../../../../../../../shared/contracts/IPivotChildColumn";
import useWizardReportDataContext from "../../../../../contexts/useWizardReportData.context";
import ChildGridGroupByFunctionsRenderer from "./cell-renderers/child-grid-group-by-functions.renderer";
import ChildGridFormatRenderer from "./cell-renderers/child-grid-format.renderer";
import WizardPivotChildColumnRowActionsRenderer from "./cell-renderers/wizard-pivot-child-column-actions.renderer";
import ChildGridColumnNameRenderer from "./cell-renderers/child-grid-column-name.renderer";


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/05/2025
 */
export default function useWizardPivotChildColumnService() {

    const { availableColumns } = useWizardReportDataContext();

    const getColDefs = useMemo((): ColDef<IPivotChildColumn>[] => {
        return [
            {
                rowDrag: true,
                headerName: "Actions", editable: false,
                resizable: false, minWidth: 216,
                cellRenderer: WizardPivotChildColumnRowActionsRenderer,
            },
            {
                editable: false, field: "childColumnName", headerName: "Pivot Child Column", initialFlex: 1,
                cellStyle: { padding: "0px" },
                cellRenderer: ChildGridColumnNameRenderer,
            },
            {
                editable: false, cellStyle: { padding: "0px" },
                field: "childColumnGroupByValue", headerName: "Group Function", initialFlex: 1,
                cellRenderer: ChildGridGroupByFunctionsRenderer,
            },
            {
                editable: false, cellStyle: { padding: "0px" },
                field: "childColumnFormatValue", headerName: "Format", initialFlex: 1,
                cellRenderer: ChildGridFormatRenderer,
            },
            {
                editable: true, field: "childColumnDescription",
                headerName: "Header Text", initialFlex: 1,
                onCellValueChanged: (event) => {
                    if (event.node && event.data) {
                        event.node.setDataValue("childColumnDescription", event.newValue);
                        event.api.dispatchEvent({ type: "rowDataUpdated" });
                    }
                },
            },

        ];
    }, [availableColumns]);

    const initializeRows = (
        gridApi: TGridApi<IPivotChildColumn>,
        pivotChildColumns?: IPivotChildColumn[] | null
    ): boolean => {

        let newRows: IPivotChildColumn[] = [];
        if (gridApi) {

            // clear residues
            gridApi.updateGridOptions({ rowData: [] })

            const availColNames = availableColumns.map(x => x.columnName);
            newRows = (pivotChildColumns || []).filter(selCol =>
                selCol.childColumnName
                && availColNames.includes(selCol.childColumnName)
            );

            const length = gridApi.getDisplayedRowCount();
            gridApi.applyTransaction({
                addIndex: length,
                add: [...newRows],
            });

            // refresh grid
            gridApi.refreshCells();
        }

        //--- return new Rows
        return newRows.length > 0;
    };

    const getNewRow = (gridApi: TGridApi<IPivotChildColumn>) => {
        // const hasFunctions = isFunctionsApplied(gridApi);
        return {
            childColumnName: null,
            childColumnDescription: null,
            childColumnGroupByValue: null,
            childColumnFormatValue: null,
        } as IPivotChildColumn;
    };

    const insertRow = (gridApi: TGridApi<IPivotChildColumn>, rowIndex?: number | null) => {
        if (gridApi) {
            const length = gridApi.getDisplayedRowCount();
            gridApi.applyTransaction({
                addIndex: (rowIndex === undefined || rowIndex === null) ? length : rowIndex,
                add: [getNewRow(gridApi)],
            });
            gridApi.refreshCells();
        }
    }

    const removeRow = (gridApi: TGridApi<IPivotChildColumn>, nodeData?: IPivotChildColumn) => {
        if (gridApi && nodeData) {
            gridApi.applyTransaction({
                remove: [nodeData]
            });
        }
    }


    // ---
    return {
        getColDefs,
        initializeRows, getNewRow,
        insertRow, removeRow,
    }
}